/**
 * A mandate's production range (`ProductionRange`) between the wire and the month pickers.
 *
 * The wire carries whole months as dates — the first day of the first month, the last day of the
 * last. The month pickers (`MonthPickerComponent`) read and write `YYYY-MM`, which is also a
 * month's period label, so `periodUtils` does the calendar arithmetic.
 */

import type { ProductionRange } from "@/forwards2/forwardsTypes";
import { periodLastDay, periodStartDate } from "@/forwards2/periodUtils";

/** `YYYY-MM` of a wire date, or `""` for an open end, as a month input takes it. */
export function monthOfDate(date: string | null): string {
  return date === null ? "" : date.slice(0, 7);
}

/** The wire's `production_from` for a picked `YYYY-MM`: its first day; null for a cleared input. */
export function firstDayOfMonth(month: string): string | null {
  return month === "" ? null : periodStartDate(month);
}

/** The wire's `production_until` for a picked `YYYY-MM`: its last day; null for a cleared input. */
export function lastDayOfMonth(month: string): string | null {
  return month === "" ? null : periodLastDay(month);
}

/**
 * Whether a `YYYY-MM`..`YYYY-MM` window lies within the range, both inclusive — the check the
 * pay-as-produced create makes on a new contract's window.
 */
export function windowWithinRange(
  firstMonth: string,
  lastMonth: string,
  range: ProductionRange
): boolean {
  const from = monthOfDate(range.production_from);
  const until = monthOfDate(range.production_until);
  return (
    (from === "" || firstMonth >= from) && (until === "" || lastMonth <= until)
  );
}
